import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Compass, Lightbulb, Users } from 'lucide-react';

const About = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: Compass,
      title: t('Curiosité', 'Neugier'),
      description: t(
        "L'envie de découvrir autre chose et de sortir de ma zone de confort.",
        "Der Wunsch, etwas anderes zu entdecken und meine Komfortzone zu verlassen."
      )
    },
    {
      icon: Heart,
      title: t('Authenticité', 'Authentizität'),
      description: t(
        "Partager mon vécu sans filtre, avec ses hauts et ses bas.",
        "Meine Erfahrungen ungefiltert teilen, mit Höhen und Tiefen."
      )
    },
    {
      icon: Lightbulb,
      title: t('Apprentissage', 'Lernen'),
      description: t(
        "Chaque expérience, même difficile, est une leçon à retenir.",
        "Jede Erfahrung, auch die schwierigen, ist eine Lektion."
      )
    },
    {
      icon: Users,
      title: t('Partage', 'Teilen'),
      description: t(
        "Aider ceux qui hésitent encore à faire le premier pas.",
        "Denen helfen, die noch zögern, den ersten Schritt zu tun."
      )
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-200 rounded-full blur-3xl opacity-40"></div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative animate-scaleIn">
                <img
                  src="/josias-about.jpg"
                  alt={t("Josias en Allemagne", "Josias in Deutschland")}
                  className="rounded-3xl shadow-2xl w-full object-cover"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-yellow-400 rounded-2xl -z-10"></div>
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-blue-600 rounded-2xl -z-10"></div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block animate-fadeInUp">
                {t('À propos', 'Über mich')}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 animate-fadeInUp delay-100 font-outfit">
                {t('Qui suis-je ?', 'Wer bin ich?')}
              </h1>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed animate-fadeInUp delay-200">
                <p>
                  {t(
                    "Je suis un jeune qui a décidé de partir en Allemagne avec plus de questions que de réponses. Je n'avais pas tout prévu, pas tout compris à l'avance, mais j'avais cette envie profonde de découvrir autre chose et de sortir de ma zone de confort.",
                    "Ich bin ein junger Mensch, der beschlossen hat, mit mehr Fragen als Antworten nach Deutschland zu gehen. Ich hatte nicht alles geplant, nicht alles im Voraus verstanden, aber ich hatte diesen tiefen Wunsch, etwas anderes zu entdecken und meine Komfortzone zu verlassen."
                  )}
                </p>
                <p>
                  {t(
                    "Cette expérience m'a appris à m'adapter, à faire face à l'inconnu, à prendre confiance en moi et à voir le monde différemment. Elle m'a aussi montré que partir n'est jamais parfait, mais toujours formateur.",
                    "Diese Erfahrung hat mich gelehrt, mich anzupassen, dem Unbekannten zu begegnen, Selbstvertrauen zu gewinnen und die Welt anders zu sehen. Sie hat mir auch gezeigt, dass Aufbrechen nie perfekt ist, aber immer lehrreich."
                  )}
                </p>
                <p className="font-medium text-slate-900">
                  {t(
                    "Ce blog, je l'ai créé pour raconter cette aventure et pour montrer que, même avec des doutes et des peurs, partir est possible.",
                    "Diesen Blog habe ich erstellt, um dieses Abenteuer zu erzählen und zu zeigen, dass Aufbrechen möglich ist, auch mit Zweifeln und Ängsten."
                  )}
                </p>
              </div>

              <div className="mt-10 animate-fadeInUp delay-300">
                <Link to="/experience">
                  <Button 
                    size="lg"
                    className="btn-primary rounded-full px-8 py-6 text-base font-bold"
                    data-testid="about-cta"
                  >
                    {t('Découvrir mon parcours', 'Meine Reise entdecken')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block">
              {t('Mes valeurs', 'Meine Werte')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit">
              {t('Ce qui me guide', 'Was mich leitet')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-200 card-hover"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-outfit">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 font-outfit">
            {t('Ma mission', 'Meine Mission')}
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed mb-10">
            {t(
              "Ce site s'adresse surtout aux jeunes qui rêvent de partir, de vivre autre chose, mais qui hésitent encore à faire le premier pas. Pas de discours parfait, pas de parcours idéalisé : juste du vécu, sans filtre, et des conseils concrets.",
              "Diese Website richtet sich vor allem an junge Menschen, die davon träumen, aufzubrechen und etwas anderes zu erleben, aber noch zögern, den ersten Schritt zu tun. Keine perfekten Reden, keine idealisierten Wege: nur echte Erfahrungen, ungefiltert, und konkrete Tipps."
            )}
          </p>
          <Link to="/contact">
            <Button 
              size="lg"
              className="btn-secondary rounded-full px-10 py-6 text-base font-bold"
              data-testid="mission-cta"
            >
              {t('Me contacter', 'Kontakt aufnehmen')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default About;
